import type {
    CurrencyCode,
    JobStatus,
    MessageTemplateKey,
    PaymentMethod,
    StaffStatus
} from '@/types'

export const STORAGE_KEY = 'washline:state:v1'

export const ACTIVE_JOB_STATUSES: JobStatus[] = ['waiting', 'assigned', 'washing', 'quality_check']

export const WORKING_JOB_STATUSES: JobStatus[] = ['assigned', 'washing', 'quality_check']

export const JOB_FLOW: JobStatus[] = ['waiting', 'assigned', 'washing', 'quality_check', 'completed']

export type Tone = 'orange' | 'violet' | 'blue' | 'teal' | 'green' | 'slate' | 'amber' | 'red'

export const JOB_STATUS_META: Record<JobStatus, { label: string, tone: Tone, description: string }> = {
    waiting: { label: 'Waiting', tone: 'orange', description: 'In the queue for a free staff member' },
    assigned: { label: 'Assigned', tone: 'violet', description: 'Staff assigned, wash not started yet' },
    washing: { label: 'Washing', tone: 'blue', description: 'Wash in progress' },
    quality_check: { label: 'Quality Check', tone: 'teal', description: 'Final inspection before handover' },
    completed: { label: 'Completed', tone: 'green', description: 'Wash finished and billed' },
    cancelled: { label: 'Cancelled', tone: 'slate', description: 'Removed from the queue' }
}

export const STAFF_STATUS_META: Record<StaffStatus, { label: string, tone: Tone }> = {
    available: { label: 'Available', tone: 'green' },
    busy: { label: 'Busy', tone: 'blue' },
    break: { label: 'Break', tone: 'amber' },
    inactive: { label: 'Inactive', tone: 'slate' }
}

export const PAYMENT_METHODS: { value: PaymentMethod, label: string }[] = [
    { value: 'cash', label: 'Cash' },
    { value: 'upi', label: 'UPI' },
    { value: 'card', label: 'Card' },
    { value: 'other', label: 'Other' }
]

export const PAYMENT_METHOD_LABEL: Record<PaymentMethod, string> = {
    cash: 'Cash',
    upi: 'UPI',
    card: 'Card',
    other: 'Other'
}

export type CurrencyMeta = {
    code: CurrencyCode
    label: string
    locale: string
    decimals: number
}

export const CURRENCIES: CurrencyMeta[] = [
    { code: 'INR', label: 'Indian Rupee (INR)', locale: 'en-IN', decimals: 2 },
    { code: 'AED', label: 'UAE Dirham (AED)', locale: 'en-AE', decimals: 2 },
    { code: 'SAR', label: 'Saudi Riyal (SAR)', locale: 'en-GB', decimals: 2 },
    { code: 'QAR', label: 'Qatari Riyal (QAR)', locale: 'en-GB', decimals: 2 },
    { code: 'OMR', label: 'Omani Rial (OMR)', locale: 'en-GB', decimals: 3 },
    { code: 'KWD', label: 'Kuwaiti Dinar (KWD)', locale: 'en-GB', decimals: 3 },
    { code: 'BHD', label: 'Bahraini Dinar (BHD)', locale: 'en-GB', decimals: 3 },
    { code: 'USD', label: 'US Dollar (USD)', locale: 'en-US', decimals: 2 },
    { code: 'EUR', label: 'Euro (EUR)', locale: 'en-IE', decimals: 2 },
    { code: 'GBP', label: 'British Pound (GBP)', locale: 'en-GB', decimals: 2 }
]

export const DEFAULT_TEMPLATES: Record<MessageTemplateKey, string> = {
    received: 'Hello! Your vehicle {vehicle} has been received for {service}. Our team has started processing your vehicle.\n\n{business}',
    completed: 'Your vehicle {vehicle} is ready! Thank you for choosing our car wash service.\n\n{business}',
    payment: 'Payment received successfully. Thank you for visiting us!\n\n{business}'
}

export const MESSAGE_TEMPLATES: { key: MessageTemplateKey, label: string, hint: string }[] = [
    { key: 'received', label: 'Vehicle received', hint: 'When a vehicle is registered' },
    { key: 'completed', label: 'Wash completed', hint: 'When the vehicle is ready' },
    { key: 'payment', label: 'Payment confirmation', hint: 'After the bill is paid' }
]

export const TEMPLATE_VARIABLES: { key: string, label: string }[] = [
    { key: 'vehicle', label: 'Vehicle number' },
    { key: 'model', label: 'Vehicle model' },
    { key: 'service', label: 'Service' },
    { key: 'amount', label: 'Bill amount' },
    { key: 'invoice', label: 'Invoice number' },
    { key: 'customer', label: 'Customer name' },
    { key: 'business', label: 'Business name' }
]

export const OVERDUE_GRACE_MINUTES = 5

export const LONG_WAIT_MINUTES = 15

export const LONG_BREAK_MINUTES = 30
